import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { AlertCircle, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
type AuthMode = "signin" | "reset";

const GoogleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 533.5 544.3"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path
      fill="#4285f4"
      d="M533.5 278.4c0-18.5-1.5-37-4.7-55H272v104h147.5c-6.3 34-25 63.6-53.3 83.1v68h86.2c50.5-46.5 80.1-115 80.1-200.1z"
    />
    <path
      fill="#34a853"
      d="M272 544.3c72.1 0 132.8-23.9 177.1-64.8l-86.2-68c-23.9 16.2-54.5 25.6-90.9 25.6-69.9 0-129.1-47.1-150.3-110.4h-89.4v69.5c44 87.4 134.6 148.1 239.7 148.1z"
    />
    <path
      fill="#fbbc04"
      d="M121.7 326.7c-10.5-31.5-10.5-65.6 0-97.1v-69.5h-89.4c-38.4 76.2-38.4 166.8 0 243z"
    />
    <path
      fill="#ea4335"
      d="M272 107.7c37.4-.6 73.2 13.7 100.6 40.1l75.1-75.1C404.8 28.6 344.1 4.8 272 4.8 166.9 4.8 76.3 65.5 32.3 152.9l89.4 69.5C142.9 159.1 202.1 112 272 112z"
    />
  </svg>
);

export default function AdminLogin() {
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    loading,
    signInWithGoogle,
    signInWithPassword,
    sendPasswordReset,
  } = useAdminAuth();

  const [authMode, setAuthMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user && isAdmin) {
      navigate("/admin");
    }
  }, [user, isAdmin, loading, navigate]);

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error.message || "Erro ao fazer login com Google");
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      toast.error("Informe um e-mail válido");
      return;
    }

    if (authMode !== "reset" && password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    try {
      setSubmitting(true);
      setInfoMessage(null);

      if (authMode === "signin") {
        await signInWithPassword(email, password);
        toast.success("Login realizado com sucesso");
      }

      if (authMode === "reset") {
        await sendPasswordReset(email);
        setInfoMessage("Enviamos um e-mail com instruções para redefinir sua senha.");
        toast.success("E-mail de recuperação enviado");
        setAuthMode("signin");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao processar solicitação");
    } finally {
      setSubmitting(false);
    }
  };

  const renderPrimaryButtonLabel = () => {
    switch (authMode) {
      case "reset":
        return "Enviar instruções";
      default:
        return "Entrar";
    }
  };

  const canSubmit = () => {
    if (authMode === "reset") {
      return Boolean(email);
    }

    return Boolean(email && password);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="flex min-h-screen items-center justify-center px-4 py-16 sm:px-6 lg:px-8">
        <Card className="w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="grid gap-0 md:grid-cols-[1.05fr_1fr]">
            <div className="relative hidden overflow-hidden bg-slate-900/5 md:flex">
              <img
                src="/lovable-uploads/5d0bfcd5-8f46-490c-8e19-f0153b1a59b3.png"
                alt="Produção fotográfica Toc Toc"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/0 to-slate-900/40" />
              <div className="relative z-10 flex h-full w-full flex-col justify-between p-10 text-toctoc-brown">
                <div className="flex items-center gap-3 text-toctoc-gray-medium">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/70 shadow-sm">
                    <Sparkles className="h-6 w-6 text-toctoc-orange" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.28em] text-toctoc-gray-medium/70">
                      Área Administrativa
                    </p>
                    <h2 className="mt-1 text-2xl font-bold text-toctoc-brown">Painel Administrativo Toc Toc</h2>
                  </div>
                </div>
                <div className="mt-auto space-y-6">
                  <img
                    src="/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
                    alt="Logo Toc Toc"
                    className="h-12 w-auto"
                  />
                  <p className="text-sm text-toctoc-gray-medium">
                    Gerencie conteúdos e experiências da plataforma Toc Toc com segurança e praticidade.
                  </p>
                  <div className="flex items-center gap-3 rounded-2xl bg-white/80 p-4 text-sm text-toctoc-gray-medium shadow-sm">
                    <ShieldCheck className="h-5 w-5 text-toctoc-orange" />
                    Acesso exclusivo para administradores autorizados.
                  </div>
                </div>
              </div>
            </div>

            <div className="relative bg-white p-6 sm:p-10 md:p-12">
              <CardHeader className="space-y-6 p-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
                      alt="Logo Toc Toc"
                      className="h-10 w-auto"
                    />
                    <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium uppercase tracking-widest text-toctoc-gray-medium">
                      Área restrita
                    </span>
                  </div>
                  <span className="text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-toctoc-gray-medium/70">
                    Admin
                  </span>
                </div>
                <div className="space-y-2">
                  <CardTitle className="text-3xl font-bold text-toctoc-brown">Seja bem-vindo ao painel Toc Toc</CardTitle>
                  <CardDescription className="text-base text-toctoc-gray-medium">
                    Acesse com sua conta corporativa do Google ou utilize e-mail e senha cadastrados para continuar.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="mt-8 space-y-8 p-0">
                <div className="space-y-3">
                  <Button
                    onClick={handleGoogleLogin}
                    className="w-full gap-3 rounded-2xl border border-slate-200 bg-white text-toctoc-brown transition hover:-translate-y-0.5 hover:border-toctoc-orange/40 hover:bg-slate-50"
                    size="lg"
                    disabled={submitting}
                  >
                    <GoogleIcon /> Entrar com Google
                  </Button>
                  <p className="text-xs text-center text-toctoc-gray-medium/80">
                    Use seu e-mail Toc Toc integrado ao Google Workspace.
                  </p>
                </div>

                <div className="flex items-center gap-4 text-toctoc-gray-medium/70">
                  <Separator className="flex-1 bg-slate-200" />
                  <span className="text-xs uppercase tracking-wide">ou</span>
                  <Separator className="flex-1 bg-slate-200" />
                </div>

                <div className="space-y-6">
                  <div className="space-y-1 text-center">
                    <h2 className="flex items-center justify-center gap-2 text-lg font-semibold text-toctoc-brown">
                      <Mail className="h-4 w-4" />
                      {authMode === "reset" ? "Recuperar acesso" : "Entrar com e-mail"}
                    </h2>
                    <p className="text-xs text-toctoc-gray-medium/80">
                      Informe seus dados de acesso para entrar no painel administrativo.
                    </p>
                  </div>

                  {infoMessage && (
                    <Alert className="border-toctoc-orange/30 bg-toctoc-orange/10 text-toctoc-brown">
                      <AlertDescription>{infoMessage}</AlertDescription>
                    </Alert>
                  )}

                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-toctoc-brown">
                        E-mail
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu-email@toctoc.com"
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        required
                      />
                    </div>

                    {authMode !== "reset" && (
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-toctoc-brown">
                          Senha
                        </Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={event => setPassword(event.target.value)}
                          minLength={6}
                          required
                        />
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full rounded-2xl bg-toctoc-orange text-white transition hover:-translate-y-0.5 hover:bg-toctoc-orange/90"
                      size="lg"
                      disabled={!canSubmit() || submitting}
                    >
                      {submitting ? "Processando..." : renderPrimaryButtonLabel()}
                    </Button>
                  </form>

                  <div className="space-y-4 text-xs text-center text-toctoc-gray-medium/80">
                    {authMode !== "signin" && (
                      <button
                        type="button"
                        className="font-medium text-toctoc-orange transition hover:text-toctoc-orange/80"
                        onClick={() => {
                          setAuthMode("signin");
                          setInfoMessage(null);
                        }}
                      >
                        Voltar para login
                      </button>
                    )}

                    {authMode === "signin" && (
                      <button
                        type="button"
                        className="font-medium text-toctoc-orange transition hover:text-toctoc-orange/80"
                        onClick={() => {
                          setAuthMode("reset");
                          setInfoMessage(null);
                        }}
                      >
                        Esqueci minha senha
                      </button>
                    )}
                  </div>

                  {authMode === "reset" && (
                    <div className="flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-50 p-4 text-[11px] text-toctoc-brown">
                      <AlertCircle className="h-4 w-4 text-toctoc-orange" />
                      Enviaremos um e-mail com instruções para redefinir sua senha.
                    </div>
                  )}
                </div>
              </CardContent>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
